import { useEffect, useMemo, useState } from 'react'
import { Check, Copy, ExternalLink, Link2, Loader2, Share2, Smartphone } from 'lucide-react'
import { toast } from 'sonner'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Textarea } from '@/components/ui/textarea'
import { AppApiError, appApiBaseUrl, appFetch } from '@/shared/api/app-client'
import type { ApiResponse, PublicShareLink } from '@/shared/api/app-types'

export type PublishClipDialogProps = {
  open: boolean
  onOpenChange: (open: boolean) => void
  title: string
  reason?: string
  projectId: number | null
  variantId: number | null
  generateShareVideo: () => Promise<Blob>
}

function defaultCaption(title: string, reason?: string) {
  const summary = reason?.trim()
  return summary ? `${title} — ${summary}` : title
}

export function PublishClipDialog({
  open,
  onOpenChange,
  title,
  reason,
  projectId,
  variantId,
  generateShareVideo,
}: PublishClipDialogProps) {
  const initialCaption = useMemo(
    () => defaultCaption(title, reason),
    [reason, title],
  )
  const [caption, setCaption] = useState(initialCaption)
  const [copied, setCopied] = useState<'link' | 'all' | null>(null)
  const [shareUrl, setShareUrl] = useState<string | null>(null)
  const [creatingShare, setCreatingShare] = useState(false)

  const supportsSystemShare = typeof window !== 'undefined'
    && typeof (window.navigator as Navigator & { share?: unknown }).share === 'function'

  function resetForOpen(nextOpen: boolean) {
    if (nextOpen) {
      setCaption(initialCaption)
      setCopied(null)
      setShareUrl(null)
    }
    onOpenChange(nextOpen)
  }

  async function copyText(value: string, kind: 'link' | 'all') {
    try {
      await navigator.clipboard.writeText(value)
      setCopied(kind)
      toast.success(kind === 'all' ? '文案和片段链接已复制' : '片段链接已复制')
    } catch {
      toast.error('复制失败，请检查浏览器剪贴板权限')
    }
  }

  async function copyForPlatform() {
    const url = await ensureShareUrl()
    if (url) await copyText(`${caption}\n\n${url}`, 'all')
  }

  async function shareWithSystem() {
    const url = await ensureShareUrl()
    if (!url || !supportsSystemShare) return
    try {
      await navigator.share({ title, text: caption, url })
    } catch (error) {
      if (error instanceof DOMException && error.name === 'AbortError') return
      toast.error('系统分享未完成，请改用复制链接')
    }
  }

  async function ensureShareUrl() {
    if (shareUrl) return shareUrl
    if (!projectId || !variantId) {
      toast.error('该片段尚未同步为可分享版本，请从项目详情页打开后再试')
      return null
    }
    setCreatingShare(true)
    try {
      const history = await appFetch<ApiResponse<{ items: PublicShareLink[] }>>(
        `/api/projects/${projectId}/share-links`,
      )
      const existing = history.data.items.find((item) =>
        item.variantId === variantId
        && item.status === 'active'
        && (!item.expiresAt || new Date(item.expiresAt).getTime() > Date.now()),
      )
      if (existing) {
        const url = `${window.location.origin}/s/${existing.shareKey}`
        setShareUrl(url)
        return url
      }

      const video = await generateShareVideo()
      const formData = new FormData()
      formData.append('title', title)
      formData.append('captionText', caption)
      formData.append('file', new File([video], `${title || 'fastcut-share'}.mp4`, { type: 'video/mp4' }))
      const upload = await fetch(
        `${appApiBaseUrl}/api/projects/${projectId}/variants/${variantId}/share-links/upload`,
        { method: 'POST', credentials: 'include', body: formData },
      )
      const payload = await upload.json().catch(() => null) as ApiResponse<{ share: PublicShareLink; url: string; reused: boolean }> | { message?: string } | null
      if (!upload.ok || !payload || !('data' in payload)) {
        throw new AppApiError(payload && 'message' in payload ? String(payload.message) : '上传分享视频失败', upload.status)
      }
      const response = payload
      setShareUrl(response.data.url)
      return response.data.url
    } catch (error) {
      toast.error(error instanceof Error ? error.message : '生成公开分享链接失败')
      return null
    } finally {
      setCreatingShare(false)
    }
  }

  useEffect(() => {
    if (!open) return
    let cancelled = false
    setCaption(initialCaption)
    setCopied(null)
    setShareUrl(null)
    if (!projectId || !variantId) return
    setCreatingShare(true)
    void appFetch<ApiResponse<{ items: PublicShareLink[] }>>(`/api/projects/${projectId}/share-links`)
      .then((response) => {
        const existing = response.data.items.find((item) =>
          item.variantId === variantId
          && item.status === 'active'
          && (!item.expiresAt || new Date(item.expiresAt).getTime() > Date.now()),
        )
        if (!cancelled && existing) {
          setShareUrl(`${window.location.origin}/s/${existing.shareKey}`)
        }
      })
      .catch(() => {
        // The publish action will present a detailed error if the user chooses to generate a link.
      })
      .finally(() => { if (!cancelled) setCreatingShare(false) })
    return () => { cancelled = true }
  }, [initialCaption, open, projectId, variantId])

  return (
    <Dialog open={open} onOpenChange={resetForOpen}>
      <DialogContent className="max-w-[620px] gap-5 p-5 sm:rounded-2xl sm:p-6">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Share2 className="size-5 text-primary" />
            分享片段
          </DialogTitle>
          <DialogDescription>生成可公开访问的成片链接，复制文案或直接通过系统分享发送。</DialogDescription>
        </DialogHeader>

        <div className="space-y-2">
          <label className="text-sm font-medium text-foreground" htmlFor="publish-caption">社交文案</label>
          <Textarea id="publish-caption" value={caption} onChange={(event) => setCaption(event.target.value)} className="min-h-[112px] text-sm leading-6" />
        </div>

        <div className="flex items-center gap-2 rounded-xl border border-border bg-background px-3 py-2">
          <Link2 className="size-4 shrink-0 text-muted-foreground" />
          <span className="min-w-0 flex-1 truncate text-xs text-muted-foreground">
            {creatingShare ? <span className="flex items-center gap-1.5 text-primary"><Loader2 className="size-3.5 animate-spin" />正在生成分享链接，请不要关闭此窗口…</span> : shareUrl ?? '生成公开成片后会显示外部分享链接'}
          </span>
          <Button variant="ghost" size="sm" className="h-7 shrink-0 gap-1 text-xs" disabled={creatingShare} onClick={() => void (async () => { const url = await ensureShareUrl(); if (url) await copyText(url, 'link') })()}>
            {copied === 'link' ? <Check className="size-3.5 text-primary" /> : <Copy className="size-3.5" />}
            复制链接
          </Button>
        </div>

        <div className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
          <Button variant="outline" onClick={() => onOpenChange(false)}>取消</Button>
          {supportsSystemShare ? (
            <Button variant="outline" onClick={() => void shareWithSystem()} disabled={creatingShare} className="gap-1.5">
              <Smartphone className="size-4" />
              系统分享
            </Button>
          ) : null}
          <Button onClick={() => void copyForPlatform()} disabled={creatingShare} className="gap-1.5">
            {creatingShare ? <Loader2 className="size-4 animate-spin" /> : copied === 'all' ? <Check className="size-4" /> : <ExternalLink className="size-4" />}
            {creatingShare ? '正在生成分享视频…' : '复制文案和链接'}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  )
}
